import { useTranslation } from 'react-i18next';

import type { ReactElement } from 'react';

import { Box, styled } from '@mui/material';

import { selectTagColorMode, selectTagStyling, selectTagVariant } from 'Config/configSlice';
import {
  PREDEFINED_TAG_COLORS,
  TAG_COLORS,
  TAG_STYLING_DEFAULTS,
  TagColorMode,
  TagVariant
} from 'Config/tagsConfig';
import { useAppSelector } from 'Store';
import { Colors } from 'Themes/colors';

import { Spacing } from '@themes/spacing';

import type { CourseFlags, CourseModel } from '../../data';

const ChipsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xsmall};
  margin: 8px 0;
`;

const getDarkerColor = (color: string): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkerR = Math.max(0, Math.floor(r * 0.7));
  const darkerG = Math.max(0, Math.floor(g * 0.7));
  const darkerB = Math.max(0, Math.floor(b * 0.7));

  return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
};

export const CourseChip = styled('span')<{
  backgroundColor: string;
  variant?: TagVariant;
  uniformColor?: string;
  borderSize?: number;
  fontSize?: number;
  colorMode?: TagColorMode;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${TAG_STYLING_DEFAULTS.padding};
  border-radius: ${TAG_STYLING_DEFAULTS.borderRadius}px;
  font-size: ${({ fontSize = TAG_STYLING_DEFAULTS.fontSize }) => fontSize}px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: ${TAG_STYLING_DEFAULTS.letterSpacing}px;
  cursor: default;
  pointer-events: none;
  border: ${({ borderSize = TAG_STYLING_DEFAULTS.borderSize }) => borderSize}px solid transparent;

  ${({ backgroundColor, variant = TagVariant.FILLED, uniformColor, colorMode }) => {
    const colorToUse =
      colorMode === TagColorMode.UNIFORM && uniformColor ? uniformColor : backgroundColor;

    if (variant === TagVariant.OUTLINED) {
      return `
        color: ${getDarkerColor(colorToUse)};
        background-color: ${getDarkerColor(colorToUse)}08;
        border-color: ${getDarkerColor(colorToUse)};
        font-weight: 600;
      `;
    }

    return `
      color: ${Colors.white};
      background-color: ${colorToUse};
    `;
  }}

  &:hover {
    transform: none;
    box-shadow: none;
    opacity: 1;
  }
`;

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return Math.abs(hash);
};

export const getTagColor = (
  tag: string | undefined | null,
  variant: TagVariant = TagVariant.FILLED,
  uniformColor?: string,
  colorMode?: TagColorMode
): string => {
  if (colorMode === TagColorMode.UNIFORM && uniformColor) {
    return uniformColor;
  }

  if (!tag || typeof tag !== 'string') {
    return TAG_COLORS[TAG_COLORS.length - 1];
  }

  const lowerTag = tag.toLowerCase();
  if (PREDEFINED_TAG_COLORS[lowerTag]) {
    return PREDEFINED_TAG_COLORS[lowerTag];
  }

  const hash = hashString(tag);
  return TAG_COLORS[hash % TAG_COLORS.length];
};

export const getFlagsAsTags = (flags: CourseFlags): string[] => {
  const tags: string[] = [];

  if (flags.isTrainer) {
    tags.push('Trainer');
  }
  if (flags.isParticipant) {
    tags.push('Participant');
  }
  if (flags.isCoordinator) {
    tags.push('Coordinator');
  }

  if (flags.isInSession) {
    tags.push('In Session');
  }

  if (flags.isPlanned) {
    tags.push('Planned');
  }

  if (flags.isEnded) {
    tags.push('Ended');
  }

  if (flags.isFinished) {
    tags.push('Finished');
  }

  if (flags.isDemo) {
    tags.push('Demo');
  }

  return tags;
};

type Props = {
  course: CourseModel;
  variant?: TagVariant;
};

export const CourseStatusChips = ({ course, variant }: Readonly<Props>) => {
  const { t } = useTranslation();
  const globalTagVariant = useAppSelector(selectTagVariant);
  const tagColorMode = useAppSelector(selectTagColorMode);
  const tagStyling = useAppSelector(selectTagStyling);

  const chips: ReactElement[] = [];
  const flags = course.flags;

  if (!flags) {
    return null;
  }

  if (flags.isTrainer) {
    chips.push(
      <CourseChip
        key="trainer"
        backgroundColor={getTagColor(
          'trainer',
          globalTagVariant,
          tagStyling.uniformColor,
          tagColorMode
        )}
        variant={variant}
        uniformColor={tagStyling.uniformColor}
        borderSize={tagStyling.borderSize}
        fontSize={tagStyling.fontSize}
        colorMode={tagColorMode}
      >
        {t('courses.tags.trainer')}
      </CourseChip>
    );
  }

  if (flags.isParticipant) {
    chips.push(
      <CourseChip
        key="participant"
        backgroundColor={getTagColor(
          'participant',
          globalTagVariant,
          tagStyling.uniformColor,
          tagColorMode
        )}
        variant={variant}
        uniformColor={tagStyling.uniformColor}
        borderSize={tagStyling.borderSize}
        fontSize={tagStyling.fontSize}
        colorMode={tagColorMode}
      >
        {t('courses.tags.participant')}
      </CourseChip>
    );
  }

  if (flags.isCoordinator) {
    chips.push(
      <CourseChip
        key="coordinator"
        backgroundColor={getTagColor(
          'coordinator',
          globalTagVariant,
          tagStyling.uniformColor,
          tagColorMode
        )}
        variant={variant}
        uniformColor={tagStyling.uniformColor}
        borderSize={tagStyling.borderSize}
        fontSize={tagStyling.fontSize}
        colorMode={tagColorMode}
      >
        {t('courses.tags.coordinator')}
      </CourseChip>
    );
  }

  if (flags.isInSession) {
    chips.push(
      <CourseChip
        key="in-session"
        backgroundColor={getTagColor(
          'in session',
          globalTagVariant,
          tagStyling.uniformColor,
          tagColorMode
        )}
        variant={variant}
        uniformColor={tagStyling.uniformColor}
        borderSize={tagStyling.borderSize}
        fontSize={tagStyling.fontSize}
        colorMode={tagColorMode}
      >
        {t('courses.tags.inSession')}
      </CourseChip>
    );
  }

  if (flags.isPlanned) {
    chips.push(
      <CourseChip
        key="planned"
        backgroundColor={getTagColor(
          'planned',
          globalTagVariant,
          tagStyling.uniformColor,
          tagColorMode
        )}
        variant={variant}
        uniformColor={tagStyling.uniformColor}
        borderSize={tagStyling.borderSize}
        fontSize={tagStyling.fontSize}
        colorMode={tagColorMode}
      >
        {t('courses.tags.planned')}
      </CourseChip>
    );
  }

  if (flags.isEnded) {
    chips.push(
      <CourseChip
        key="ended"
        backgroundColor={getTagColor(
          'Ended',
          globalTagVariant,
          tagStyling.uniformColor,
          tagColorMode
        )}
        variant={variant}
        uniformColor={tagStyling.uniformColor}
        borderSize={tagStyling.borderSize}
        fontSize={tagStyling.fontSize}
        colorMode={tagColorMode}
      >
        {t('courses.tags.ended')}
      </CourseChip>
    );
  }

  if (flags.isFinished) {
    chips.push(
      <CourseChip
        key="finished"
        backgroundColor={getTagColor(
          'Finished',
          globalTagVariant,
          tagStyling.uniformColor,
          tagColorMode
        )}
        variant={variant}
        uniformColor={tagStyling.uniformColor}
        borderSize={tagStyling.borderSize}
        fontSize={tagStyling.fontSize}
        colorMode={tagColorMode}
      >
        {t('courses.tags.finished')}
      </CourseChip>
    );
  }

  if (flags.isDemo) {
    chips.push(
      <CourseChip
        key="demo"
        backgroundColor={getTagColor(
          'demo',
          globalTagVariant,
          tagStyling.uniformColor,
          tagColorMode
        )}
        variant={variant}
        uniformColor={tagStyling.uniformColor}
        borderSize={tagStyling.borderSize}
        fontSize={tagStyling.fontSize}
        colorMode={tagColorMode}
      >
        {t('courses.tags.demo')}
      </CourseChip>
    );
  }

  if (chips.length === 0) {
    return null;
  }

  return <ChipsContainer>{chips}</ChipsContainer>;
};
